import {
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFormikContext } from "formik";
import Loader from "../loader/Loader";
import InfoModal from "./InfoModal";

type SubmitModalWithFormikProps = {
  title: string;
  children: React.ReactNode;
  dialogActionChildrenBefore: React.ReactNode;
  dialogActionChildrenAfter: React.ReactNode;
  closeSubmitModal: CallableFunction;
  childErrorMessage?: string;
  allowSubmit: boolean;
  size: false | Breakpoint | undefined;
  isLoading?: boolean;
  submitButtonName?: string;
  closeModalAfterSubmit?: boolean;
  showSubmitButton?: boolean;
  fullWidth?: boolean;
  allowSubmitWithoutTouch?: boolean;
  hasInfoPopup?: boolean;
  fetchInfoModalData?: CallableFunction;
};

export interface ModalTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

const BootstrapModal = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function BootstrapModalTitle(props: ModalTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        backgroundColor: "primary.main",
        color: (theme) => theme.palette.grey[50],
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[50],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const SubmitModalWithFormikAndMultipleDialogActions = (
  Props: SubmitModalWithFormikProps
) => {
  const {
    title,
    children,
    dialogActionChildrenBefore,
    dialogActionChildrenAfter,
    closeSubmitModal,
    childErrorMessage,
    size,
    isLoading = false,
    submitButtonName = "Submit",
    closeModalAfterSubmit = true,
    showSubmitButton = true,
    fullWidth = true,
    allowSubmitWithoutTouch = false,
    hasInfoPopup = false,
    fetchInfoModalData,
  } = Props;

  const { submitForm, isValid, isSubmitting, touched } = useFormikContext();

  const [isOpen, setIsOpen] = useState(true);
  const [showInfoPopup, setShowInfoPopup] = useState<boolean>(false);
  const [isInfoPopupAcknowledged, setInfoPopupAcknowledged] = useState(false);
  const [infoPopupTitle, setInfoPopupTitle] = useState<string | undefined>();
  const [infoPopupMsg, setInfoPopupMsg] = useState<string | undefined>();

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      if (childErrorMessage) {
        toast.error(childErrorMessage);
      }
    }
    return () => {
      isFunctionRan = false;
    };
  }, [childErrorMessage]);

  useEffect(() => {
    if (isInfoPopupAcknowledged) {
      handleSubmit();
    }
  }, [isInfoPopupAcknowledged]);

  const _closeSubmitModal = () => {
    setIsOpen(false);
    closeSubmitModal();
  };

  const handleClickCloseModal = () => {
    _closeSubmitModal();
  };

  const handleInfoPopupClose = () => {
    setShowInfoPopup(false);
    setInfoPopupAcknowledged(true);
  };

  const handleSubmit = async () => {
    if (hasInfoPopup && fetchInfoModalData && !isInfoPopupAcknowledged) {
      // Call the function to fetch info modal data
      const info = fetchInfoModalData();
      if (info && info.title && info.message) {
        setInfoPopupTitle(info.title);
        setInfoPopupMsg(info.message);
        setShowInfoPopup(true);
      } else {
        setInfoPopupAcknowledged(true);
      }
      return;
    } else {
      // If the modal was acknowledged or not required, proceed with submission
      await submitForm();
      if (closeModalAfterSubmit) {
        _closeSubmitModal();
      }
    }
  };

  return (
    <BootstrapModal
      open={isOpen}
      onClose={handleClickCloseModal}
      aria-labelledby="customized-dialog-title"
      maxWidth={size}
      fullWidth={fullWidth}
    >
      <BootstrapModalTitle
        id="customized-dialog-title"
        onClose={handleClickCloseModal}
      >
        <div>{title}</div>
      </BootstrapModalTitle>
      <DialogContent dividers>
        <div>{children}</div>
      </DialogContent>
      <DialogActions>
        {dialogActionChildrenBefore}
        {showSubmitButton && (
          <Button
            variant="contained"
            size="small"
            disabled={
              allowSubmitWithoutTouch
                ? !isValid
                : Object.keys(touched).length === 0 || !isValid
            }
            onClick={handleSubmit}
          >
            {submitButtonName}
          </Button>
        )}
        {dialogActionChildrenAfter}
      </DialogActions>
      <Loader isLoading={isSubmitting || isLoading} />
      {showInfoPopup && (
        <InfoModal
          open={showInfoPopup}
          title={infoPopupTitle || ""}
          onClose={handleInfoPopupClose}
          textInfo={infoPopupMsg || ""}
        />
      )}
    </BootstrapModal>
  );
};

export default SubmitModalWithFormikAndMultipleDialogActions;
